// Frontend CASL ability mirror (ADR-0005 §7.4, amended by ADR-0009).
//
// The backend is the source of truth for authorization. This module only
// hides UI affordances the current user can't use. Every protected
// operation is checked again on the server.
//
// The ability is built from the caller's effective rules, as returned by
// `GET /v1/auth/me/abilities` (ADR-0009, ADR-0023 §6). It is never built
// from role names in the `rls` claim: those would drift from the server
// as soon as a role is edited.
//
// JWT `tid` on EMRA is the tenant UUID (ADR-0004 "tid overload"). On EMR the
// same claim holds the CLINIC_ID. Rules with `${user.tenantId}` are already
// filled in by the server before they reach the browser.

import { createMongoAbility, type MongoAbility, type RawRuleOf } from '@casl/ability';

import type { AbilityRuleDto, EmraAbilitySubject, EmrAbilityAction } from '@org/contracts';

export type EmraAction = EmrAbilityAction;
export type EmraSubject = EmraAbilitySubject;
export type AppAbility = MongoAbility<[EmraAction, EmraSubject]>;

export interface AbilityCheck {
  action: EmraAction;
  subject: EmraSubject;
}

/**
 * Build the UI ability from server-computed rules. Wire rules carry plain
 * strings, so an unknown subject never fails the parse. CASL matches by
 * string, so an unknown subject simply never matches a typed check.
 */
export function buildAbility(rules: AbilityRuleDto[]): AppAbility {
  return createMongoAbility<AppAbility>(rules as RawRuleOf<AppAbility>[]);
}

/** Denies everything. Used while rules load, on error, and when signed out. */
export const EMPTY_ABILITY: AppAbility = createMongoAbility<AppAbility>([]);

/** True when every check passes. */
export function canAll(ability: AppAbility, checks: readonly AbilityCheck[]): boolean {
  return checks.every((check) => {
    return ability.can(check.action, check.subject);
  });
}
